import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Divider, IconButton, Stack, Tooltip} from '@mui/material';
import React, {Suspense, useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import FeatureTable from './FeaturesTable.jsx';
import {setCollectionVisibility} from '@actions/collections';
import {fitExtent} from '@actions/map';
import {notify} from '@actions/notifications';
import {deselectFeatureRef} from '@actions/selected-features';
import {useGetProductsCount} from '@api/products';
import {trackEvent} from '@app/analytics';
import {useGetCollection} from '@app/api/collections';
import DrawerHeader from '@app/components/common/DrawerHeader';
import {MODALS, TOUR_STAGES} from '@app/constants';
import useCollectionParams from '@app/hooks/useCollectionParams';
import MoreActionsMenu from '@components/Collections/CollectionActions/MoreActionsMenu';
import ToggleVisibilityButton from '@components/Collections/CollectionActions/ToggleVisibilityButton';
import ZoomToCollectionButton from '@components/Collections/CollectionActions/ZoomToCollectionButton';

const QuotaManagerModal = React.lazy(
  () => import('@app/components/QuotaManager/Modal')
);

const EVENT_NAME = 'Features Panel';

const FeaturesPanel = () => {
  const dispatch = useDispatch();
  const {collectionId, resetCollection, zoom} = useCollectionParams();
  const [modal, setModal] = useState('');

  const closeModal = useCallback(() => {
    setModal('');
  }, [setModal]);

  const {data: collection, error} = useGetCollection(
    {
      collectionId,
    },
    {
      enabled: !!collectionId,
    }
  );

  const {data: productsCount} = useGetProductsCount();

  useEffect(() => {
    if (error) {
      let errorMessage = `There was an error while loading collection with id ${collectionId}`;

      if (error.response?.status === 403) {
        errorMessage = `You do not have permission to view collection with id ${collectionId}`;
      }

      dispatch(
        notify({
          message: errorMessage,
          severity: 'error',
          persist: true,
        })
      );

      resetCollection();
    }
  }, [collectionId, dispatch, error, resetCollection]);

  useEffect(() => {
    if (collection) {
      dispatch(setCollectionVisibility({collection, visible: true}));
      if (collection.feature_count > 0 && collection.extent && zoom) {
        dispatch(fitExtent(collection.extent?.spatial?.bbox[0]));
      }
    }
  }, [collection, dispatch, zoom]);

  return (
    <>
      <DrawerHeader
        data-tour={TOUR_STAGES.FEATURE_HEADER}
        data-testid="features-panel-header"
        avatar={
          <>
            <Tooltip title="Back to collections">
              <IconButton
                onClick={() => {
                  trackEvent(EVENT_NAME, {
                    action: 'Close features view',
                    collectionId,
                  });
                  resetCollection();
                  dispatch(deselectFeatureRef());
                }}
                size="small"
                sx={{marginRight: 1}}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
          </>
        }
        title={collection?.title}
        action={
          collection ? (
            <Stack spacing={0.5} direction="row">
              <ToggleVisibilityButton
                collection={collection}
                disabled={collection.feature_count === 0}
              />
              <ZoomToCollectionButton
                collection={collection}
                disabled={collection.feature_count === 0}
              />
              <MoreActionsMenu
                collection={collection}
                hasProducts={productsCount > 0}
                target="collection"
              />
            </Stack>
          ) : null
        }
      />

      {collection && (
        <FeatureTable
          collection={collection}
          hasProducts={productsCount > 0}
          onReserveQuotaClick={() => {
            trackEvent(EVENT_NAME, {
              action: `Opened manage quota modal`,
              collectionId,
              featureCount: collection.feature_count,
            });
            setModal(MODALS.QUOTA_MANAGER);
          }}
        />
      )}

      <Suspense>
        <QuotaManagerModal
          open={modal === MODALS.QUOTA_MANAGER}
          onClose={closeModal}
          collection={collection}
        />
      </Suspense>
    </>
  );
};

export default FeaturesPanel;
